/* © 2024-2026 Johannes Roob. Proprietärer Code. Keine Nutzung oder Weitergabe ohne Erlaubnis. | Proprietary code. No use or distribution without permission. */

type tokenFn = (date: Date) => string;

export class TimeFormatter {
    #layout: string;
    #monthsShort: string[];
    #monthsLong: string[];
    #daysShort: string[];
    #daysLong: string[];
    #tokens: Record<string, tokenFn>;
    #sortedTokenKeys: string[];

    constructor(layout: string) {
        this.#layout = layout;

        this.#monthsShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        this.#monthsLong = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        this.#daysShort = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        this.#daysLong = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

        this.#tokens = {
            // Year
            "2006": (d: Date) => String(d.getUTCFullYear()).padStart(4, "0"),
            "06": (d: Date) => String(d.getUTCFullYear() % 100).padStart(2, "0"),

            // Month
            "January": (d: Date) => this.#monthsLong[d.getUTCMonth()],
            "Jan": (d: Date) => this.#monthsShort[d.getUTCMonth()],
            "01": (d: Date) => String(d.getUTCMonth() + 1).padStart(2, "0"),
            "1": (d: Date) => String(d.getUTCMonth() + 1),

            // Day of month
            "02": (d: Date) => String(d.getUTCDate()).padStart(2, "0"),
            "2": (d: Date) => String(d.getUTCDate()),
            "_2": (d: Date) => String(d.getUTCDate()).padStart(2, " "),

            // Weekday
            "Monday": (d: Date) => this.#daysLong[d.getUTCDay()],
            "Mon": (d: Date) => this.#daysShort[d.getUTCDay()],

            // Hour
            "15": (d: Date) => String(d.getUTCHours()).padStart(2, "0"),
            "03": (d: Date) => String(d.getUTCHours() % 12 || 12).padStart(2, "0"),
            "3": (d: Date) => String(d.getUTCHours() % 12 || 12),

            // Minute
            "04": (d: Date) => String(d.getUTCMinutes()).padStart(2, "0"),
            "4": (d: Date) => String(d.getUTCMinutes()),

            // Second
            "05": (d: Date) => String(d.getUTCSeconds()).padStart(2, "0"),
            "5": (d: Date) => String(d.getUTCSeconds()),

            // AM/PM
            "PM": (d: Date) => d.getUTCHours() < 12 ? "AM" : "PM",
            "pm": (d: Date) => d.getUTCHours() < 12 ? "am" : "pm",

            // Fractional second
            ".999999999": (d: Date) => "." + fraction(d),
            ".99999999": (d: Date) => "." + fraction(d),
            ".9999999": (d: Date) => "." + fraction(d),
            ".999999": (d: Date) => "." + fraction(d),
            ".99999": (d: Date) => "." + fraction(d),
            ".9999": (d: Date) => "." + fraction(d),
            ".999": (d: Date) => "." + fraction(d),
            ".99": (d: Date) => "." + fraction(d),
            ".9": (d: Date) => "." + fraction(d),
        };

        this.#sortedTokenKeys = Object.keys(this.#tokens)
            .sort((a, b) => {
                return b.length - a.length;
            });
    }

    format(value: any) {
        const date = toValidDate(value);

        if (date === null) {
            return "";
        }

        let result = "";
        let layout = this.#layout;

        // Walk from left to right of the layout.
        // If the layout starts with a token key, 
        // append the result of the token function to result and
        // remove the matched token key from layout.
        // Otherwise move a single character to the result, until
        // layout is empty.
        while (layout.length !== 0) {
            let match = false;

            for (const tokenKey of this.#sortedTokenKeys) {
                if (layout.startsWith(tokenKey, 0) === false) {
                    continue;
                }

                match = true;
                const replacement = this.#tokens[tokenKey](date);
                result = result + replacement;
                layout = layout.slice(tokenKey.length, undefined);
                break;
            }

            if (match === false) {
                result = result + layout[0];
                layout = layout.slice(1, undefined);
            }
        }

        return result;
    }
}

function toValidDate(value: any) {
    if (value == null || value === "") {
        return null;
    }

    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date;
}

/* 
    JS Date only has millisecond precision. Returns 000–999.
*/
function fraction(date: Date) {
    return String(date.getUTCMilliseconds()).padStart(3, "0");
}